import React, { useRef, useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { Box, IconButton } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

export default function VideoPlayer({ video }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9); // Default to horizontal aspect ratio
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Check video aspect ratio
  useEffect(() => {
    const handleAspectRatio = async () => {
      const response = await fetch(video);
      const videoBlob = await response.blob();
      const videoElement = document.createElement("video");

      videoElement.src = URL.createObjectURL(videoBlob);
      videoElement.onloadedmetadata = () => {
        const ratio = videoElement.videoWidth / videoElement.videoHeight;
        setAspectRatio(ratio);
      };
    };

    handleAspectRatio();
  }, [video]);

  const isVertical = aspectRatio < 1;

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Handle full-screen change
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: isVertical ? "240px" : "100%", // Limit width for vertical videos
        maxWidth: isVertical ? "240px" : "500px", // Max width for horizontal videos
        height: isVertical ? "400px" : "auto", // Limit height for vertical videos
        paddingTop: isVertical ? "unset" : `${100 / aspectRatio}%`, // Dynamic padding for horizontal videos only
        overflow: "hidden",
        backgroundColor: "black",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ReactPlayer
        ref={playerRef}
        url={video}
        width="100%"
        height="100%"
        controls
        playing
        style={{
          objectFit: "contain",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)", // Center the video for both orientations
        }}
      />
      <IconButton
        onClick={toggleFullScreen}
        sx={{
          position: "absolute",
          bottom: 16,
          right: 16,
          color: "white",
          zIndex: 1,
        }}
      >
        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
  );
}
